import { inject, Injectable } from '@angular/core';
import { BooktechAppService } from './booktech-app.service';
import {
  AlternativePeriodResponse,
  AvailableSlotDto,
  FirstPossibleStartResponse,
  PoolUnitDto,
  ProductAvailabilityResponse,
  ProductInputValueResponse,
  ProductOrderRelationDto,
  ProductPageResponse,
  ProductResponse,
  PwAmountResponse,
  ScheduleItemDto
} from '../model/product';

@Injectable({
  providedIn: 'root'
})
export class ProductApiService {

  bas = inject(BooktechAppService);

  private bjson(audience: string, endpoint: string, body: any = null, options: any = {}): Promise<any> {
    if (options.cid === '') {
      options = { ...options, cid: undefined };
    }
    return this.bas.ws.bjson(audience, endpoint, body, options);
  }

  async getProduct(cid: string, id: number): Promise<ProductResponse> {
    return this.bjson('public', `products/${id}`, null, { method: 'get', cid });
  }

  async getProductPage(cid: string, id: number): Promise<ProductPageResponse> {
    return this.bjson('public', `products/${id}/page`, null, { method: 'get', cid });
  }

  async getAmount(cid: string, id: number, params: AmountParams): Promise<PwAmountResponse> {
    return this.bjson('public', `products/${id}/amount`, null, {
      method: 'get',
      cid,
      params: this.buildAmountParams(params)
    });
  }

  async getAvailability(cid: string, id: number, params: PeriodParams): Promise<ProductAvailabilityResponse> {
    return this.bjson('public', `products/${id}/availability`, null, {
      method: 'get',
      cid,
      params: this.buildPeriodParams(params)
    });
  }

  async getFirstValidEndDate(cid: string, id: number, from: string, quantity: number, guests: string): Promise<string | null> {
    const res = await this.bjson('public', `products/${id}/first-valid-end-date`, null, {
      method: 'get',
      cid,
      params: { from, quantity, guests, maxDays: 30 }
    });
    return res ?? null;
  }

  async getSchedule(cid: string, id: number, params: PeriodParams): Promise<ScheduleItemDto[]> {
    return this.bjson('public', `products/${id}/schedule`, null, {
      method: 'get',
      cid,
      params: this.buildPeriodParams(params)
    });
  }

  async getTimeItems(cid: string, id: number, params: PeriodParams): Promise<ScheduleItemDto[]> {
    return this.bjson('public', `products/${id}/time-items-available`, null, {
      method: 'get',
      cid,
      params: this.buildPeriodParams(params)
    });
  }

  async getSelectableDates(cid: string, id: number, params: SelectableDatesParams): Promise<number[]> {
    return this.bjson('public', `products/${id}/selectable-dates`, null, {
      method: 'get',
      cid,
      params: this.buildSelectableDatesParams(params)
    });
  }

  async getFirstPossiblePeriod(cid: string, id: number, params: BaseParams): Promise<FirstPossibleStartResponse> {
    return this.bjson('public', `products/${id}/first-possible-period`, null, {
      method: 'get',
      cid,
      params: this.buildBaseParams(params)
    });
  }

  async getInitialPeriod(cid: string, id: number, params: InitialPeriodParams): Promise<FirstPossibleStartResponse> {
    return this.bjson('public', `products/${id}/initial-period`, null, {
      method: 'get',
      cid,
      params: this.buildInitialPeriodParams(params)
    });
  }

  async getAlternativePeriods(cid: string, id: number, params: AlternativePeriodsParams): Promise<AlternativePeriodResponse[]> {
    return this.bjson('public', `products/${id}/alternative-periods`, null, {
      method: 'get',
      cid,
      params: this.buildAlternativePeriodsParams(params)
    });
  }

  async getInputValues(cid: string, id: number, guests: string = ''): Promise<ProductInputValueResponse[]> {
    return this.bjson('public', `products/${id}/input-values`, null, {
      method: 'get',
      cid,
      params: { guests }
    });
  }

  async getAvailableSlots(cid: string, id: number, params: AvailableSlotsParams): Promise<AvailableSlotDto[]> {
    return this.bjson('public', `products/${id}/available-slots`, null, {
      method: 'get',
      cid,
      params: this.buildAvailableSlotsParams(params)
    });
  }

  async getAccessories(cid: string, id: number, params: PeriodParams): Promise<ProductOrderRelationDto[]> {
    return this.bjson('public', `products/${id}/accessories`, null, {
      method: 'get',
      cid,
      params: this.buildPeriodParams(params)
    });
  }

  async getGroupItems(cid: string, id: number, params: PeriodParams): Promise<ProductOrderRelationDto[]> {
    return this.bjson('public', `products/${id}/group-items`, null, {
      method: 'get',
      cid,
      params: this.buildPeriodParams(params)
    });
  }

  async getPackageItems(cid: string, id: number, params: PeriodParams): Promise<ProductOrderRelationDto[]> {
    return this.bjson('public', `products/${id}/package-items`, null, {
      method: 'get',
      cid,
      params: this.buildPeriodParams(params)
    });
  }

  async getPoolUnits(cid: string, id: number, params: PeriodParams): Promise<PoolUnitDto[]> {
    return this.bjson('public', `products/${id}/pool-units`, null, {
      method: 'get',
      cid,
      params: this.buildPeriodParams(params)
    });
  }

  private buildAmountParams(params: AmountParams): any {
    const result: any = this.buildPeriodParams(params);
    if (params.startTime !== undefined) result.startTime = params.startTime;
    if (params.endTime !== undefined) result.endTime = params.endTime;
    if (params.routeFromId !== undefined) result.routeFromId = params.routeFromId;
    if (params.routeToId !== undefined) result.routeToId = params.routeToId;
    return result;
  }

  private buildPeriodParams(params: PeriodParams): any {
    const result: any = this.buildBaseParams(params);
    if (params.from) result.from = params.from;
    if (params.to) result.to = params.to;
    return result;
  }

  private buildBaseParams(params: BaseParams): any {
    const result: any = {};
    if (params.quantity !== undefined) result.quantity = params.quantity;
    if (params.guests !== undefined) result.guests = params.guests;
    return result;
  }

  private buildSelectableDatesParams(params: SelectableDatesParams): any {
    const result: any = this.buildBaseParams(params);
    if (params.maxResults !== undefined) result.maxResults = params.maxResults;
    return result;
  }

  private buildInitialPeriodParams(params: InitialPeriodParams): any {
    const result: any = this.buildBaseParams(params);
    if (params.fromWeekday !== undefined) result.fromWeekday = params.fromWeekday;
    if (params.toWeekday !== undefined) result.toWeekday = params.toWeekday;
    return result;
  }

  private buildAlternativePeriodsParams(params: AlternativePeriodsParams): any {
    const result: any = this.buildPeriodParams(params);
    result.maxDiff = params.maxDiff;
    if (params.maxResults !== undefined) result.maxResults = params.maxResults;
    return result;
  }

  private buildAvailableSlotsParams(params: AvailableSlotsParams): any {
    const result: any = this.buildPeriodParams(params);
    if (params.maxSlots !== undefined) result.maxSlots = params.maxSlots;
    return result;
  }
}

export interface BaseParams {
  quantity?: number;
  guests?: string;
}

export interface PeriodParams extends BaseParams {
  from?: string;
  to?: string;
}

export interface AmountParams extends PeriodParams {
  startTime?: number;
  endTime?: number;
  routeFromId?: number;
  routeToId?: number;
}

export interface SelectableDatesParams extends BaseParams {
  maxResults?: number;
}

export interface InitialPeriodParams extends BaseParams {
  fromWeekday?: number;
  toWeekday?: number;
}

export interface AlternativePeriodsParams extends PeriodParams {
  maxDiff: number;
  maxResults?: number;
}

export interface AvailableSlotsParams extends PeriodParams {
  maxSlots?: number;
}
